/**
 * Phase 4E — storage architecture for previews vs favorites.
 *
 * Layers (do not conflate ownership):
 * 1. artQueue React state — session artworks (html + optional preview/display blob URLs)
 * 2. previewBlobLifecycle — Phase 4A ownership/revoke for in-memory blob: URLs
 * 3. previewCache Map — session-only key→blob URL (same process; never durable)
 * 4. localStorage `gallery` — favorites (full generated HTML + config); survives reload
 * 5. Legacy IndexedDB `EOLPreviewCache` — REMOVED; stored blob: strings that die on reload
 *
 * Decision (Option A): do not persist preview image bytes or blob URL strings.
 * Same-session reuse uses the in-memory Map + Phase 4A ownership.
 * Favorites keep HTML because exact pixel reconstruction from config alone is not proven.
 */

export const LEGACY_PREVIEW_IDB_NAME = 'EOLPreviewCache';

/** True if a string is a blob: object URL (session-scoped; never durable storage). */
export function isBlobUrlReference(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('blob:');
}

/**
 * Values safe to treat as durable image references.
 * blob: is never durable. data: / http(s) would be, but we do not persist previews.
 */
export function isDurableImageReference(value: unknown): boolean {
  if (typeof value !== 'string' || !value) return false;
  if (isBlobUrlReference(value)) return false;
  return (
    value.startsWith('data:image/') ||
    value.startsWith('https://') ||
    value.startsWith('http://')
  );
}

/** Reject blob: (and other non-durable) values before any persistence write. */
export function assertNotPersistingBlobUrl(value: unknown, context: string): void {
  if (isBlobUrlReference(value)) {
    throw new Error(
      `[previewPersistence] refused to persist blob: URL (${context}). ` +
        'Object URLs are session-scoped and invalid after reload/revoke.'
    );
  }
}

/**
 * One-shot migration: delete the legacy preview IndexedDB database that stored
 * blob: URL strings under store `previews` / field `dataUrl`.
 * Safe to call on every app load; ignores missing DB / private-mode errors.
 */
export function purgeLegacyPreviewIndexedDb(): Promise<boolean> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined' || !indexedDB.deleteDatabase) {
        resolve(false);
        return;
      }
      const req = indexedDB.deleteDatabase(LEGACY_PREVIEW_IDB_NAME);
      req.onsuccess = () => resolve(true);
      req.onerror = () => resolve(false);
      req.onblocked = () => resolve(false);
    } catch {
      resolve(false);
    }
  });
}

export type FavoriteGalleryItem = {
  id: string;
  html: string;
  config?: unknown;
  timestamp?: number;
  [key: string]: unknown;
};

export const FAVORITES_LOCAL_STORAGE_KEY = 'gallery';

export type FavoritesPersistResult =
  | { ok: true }
  | { ok: false; reason: 'quota' | 'serialize' | 'unavailable' | 'unknown'; error?: unknown };

/** Load favorites from localStorage. Returns [] on missing/corrupt data. */
export function loadFavoritesFromLocalStorage(
  storage: Storage | null | undefined = typeof localStorage !== 'undefined' ? localStorage : null
): FavoriteGalleryItem[] {
  if (!storage) return [];
  try {
    const raw = storage.getItem(FAVORITES_LOCAL_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (item) => item && typeof item === 'object' && typeof (item as FavoriteGalleryItem).id === 'string'
    ) as FavoriteGalleryItem[];
  } catch {
    return [];
  }
}

/** Persist favorites JSON. Handles quota / private mode without throwing. */
export function saveFavoritesToLocalStorage(
  items: FavoriteGalleryItem[],
  storage: Storage | null | undefined = typeof localStorage !== 'undefined' ? localStorage : null
): FavoritesPersistResult {
  if (!storage) {
    return { ok: false, reason: 'unavailable' };
  }
  let serialized: string;
  try {
    serialized = JSON.stringify(items);
  } catch (error) {
    return { ok: false, reason: 'serialize', error };
  }
  try {
    storage.setItem(FAVORITES_LOCAL_STORAGE_KEY, serialized);
    return { ok: true };
  } catch (error) {
    const name = (error as { name?: string })?.name || '';
    if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
      return { ok: false, reason: 'quota', error };
    }
    return { ok: false, reason: 'unknown', error };
  }
}

/** Approximate UTF-16 storage cost of favorite HTML payloads (chars × 2 bytes). */
export function estimateFavoritesHtmlStorageBytes(items: FavoriteGalleryItem[]): number {
  let chars = 0;
  for (const item of items) {
    if (typeof item.html === 'string') chars += item.html.length;
  }
  return chars * 2;
}

/**
 * Session preview cache: Map from previewKey → blob: URL.
 * Must never be written to IndexedDB or localStorage.
 */
export function setSessionPreviewCacheEntry(
  cache: Map<string, string>,
  previewKey: string,
  url: string
): void {
  // Session Map may hold blob: URLs; that is intentional and non-durable.
  cache.set(previewKey, url);
}

export function getSessionPreviewCacheEntry(
  cache: Map<string, string>,
  previewKey: string
): string | undefined {
  const url = cache.get(previewKey);
  if (!url) return undefined;
  // Defensive: never treat a non-string as a hit.
  if (typeof url !== 'string') {
    cache.delete(previewKey);
    return undefined;
  }
  return url;
}
